<script>
import { mapState } from 'vuex'
import Dropzone from '@/components/upload/Dropzone'
import PdfListElement from './pdfListElement/PdfListElement.vue'
import FileUploadDialog from './FileUploadDialog.vue'
import UploadDialog from './UploadDialog.vue'
import AlertDialog from './AlertDialog.vue'
import { FileUploader } from '@/helpers/fileUploader.js'
import uploadJson from '@/data/json/upload/upload.json'
export default {
  name: 'UploadComponent',
  components: {
    Dropzone,
    PdfListElement,
    FileUploadDialog,
    UploadDialog,
    AlertDialog
  },
  props: {
    letterCategory: {
      type: String
    },
    loading: {
      type: Boolean
    }
  },
  data: () => ({
    content: uploadJson.data,
    uploadedFiles: [], // without metaData
    files: [], // with metaData
    filesInTransfer: {
      inTransfer: false,
      count: 0,
      actual: 0,
      files: []
    },
    alertList: {
      UserHasNoPermission: [],
      InvalidMetaTag: [],
      DuplicateFiles: [],
      PageCountValidationFailed: [],
      FileSizeTooLarge: [],
      WhiteLineWithoutSerialSeq: [],
      MetaTagOnSerialLetter: [],
      XMP: [],
      WhiteLineNotParsable: [],
      PdfFileEncrypted: []
    },
    showUploadDialog: false,
    showAlertDialog: false,
    showDuplicateFilesNote: false
  }),
  computed: {
    ...mapState({
      userSettings: state => state.settings
    })
  },
  created() {
    this.FileUploader = new FileUploader(this.letterCategory)
    this.FileUploader.forceFillMetaData =
      this.userSettings.locked && this.letterCategory !== 'series' // if set to true, uploadDialog will be skipped
    this.filesInTransfer = this.FileUploader.FilesInTransfer
  },
  methods: {
    fileElementChanged(event) {
      this.files[event.index] = event.file
      this.$emit('update:files', this.files)
    },
    addFile(file) {
      //if file is not already in the arr then add it
      let isInFilesList = false
      this.files.forEach(elem => {
        if (elem.name === file.name) {
          isInFilesList = true
        }
      })
      if (!isInFilesList) {
        this.files.push(file)
        this.$emit('update:files', this.files)
      }
    },
    deleteFile(index) {
      this.files.splice(index, 1)
      this.$emit('update:files', this.files)
    },
    upload(files) {
      let fileArray = this.checkUniqueFiles(files)
      this.FileUploader.upload(fileArray)
        .then(uploadResult => {
          this.uploadedFiles = uploadResult.files
          this.showUploadDialog = uploadResult.needSettings
          this.alertList = uploadResult.alertList
          // uploadResult has files filled with metaData
          // add to List
          uploadResult.metaDataFiles.forEach(metaDataFile => {
            this.addFile(metaDataFile)
          })
        })
        .catch(alert => {
          this.alertList = alert
        })
        .finally(() => {
          this.checkAlert()
        })
    },
    checkAlert() {
      //open if uploadDialog is close and there is at least one file with error
      if (
        !this.showUploadDialog &&
        Object.values(this.alertList).some(listItem => listItem.length)
      ) {
        this.showAlertDialog = true
      }
    },
    async addMetaDataToFiles(fileSettings) {
      const files = this.uploadedFiles
      let promises = []
      let chunkPromises = []

      for (var i = 0; i < files.length; i++) {
        this.addMetaData(files[i], fileSettings)
        this.addFile(files[i])
      }
      await this.$nextTick()
      this.showUploadDialog = false
      const chunks = this.FileUploader.sliceIntoChunks(
        this.$refs.PdfListElements.filter(element =>
          files.map(file => file.id).includes(element.$attrs['file-id'])
        ),
        this.FileUploader.chunkSize
      )
      // for-loop is used
      // forEach would be async and don't waits vor await / promise
      for (var chunk = 0; chunk < chunks.length; chunk++) {
        chunkPromises = []
        for (let i = 0; i < chunks[chunk].length; i++) {
          const chunkRef = chunks[chunk][i]
          const promise = chunkRef.setMetaData()
          chunkPromises.push(promise)
          promises.push(promise)
        }
        await Promise.all(chunkPromises)
      }
      //wait until metadata-tag of all files are checked
      await Promise.all(promises)

      this.checkAlert()
    },
    addMetaData(file, fileSettings) {
      file.isSerialLetter = this.letterCategory === 'series' ? true : false
      file.splitAfterNPages = fileSettings.splitAfterNPages
      file.ignoreCase = fileSettings.ignoreCase
      file.splitAfterTextPattern = fileSettings.splitAfterTextPattern
      file.SplitType = fileSettings.SplitType
      file.ColorMode = fileSettings.ColorMode
      file.PrintMode = fileSettings.PrintMode
      file.FoldType = fileSettings.FoldType
      file.detectionArea = fileSettings.detectionArea
      file.CostCenter = fileSettings.CostCenter
    },
    // checks if new files was allready uploaded
    checkUniqueFiles(filesToUpload) {
      let uniqueFiles = []
      let duplicateFiles = []
      for (var i = 0; i < filesToUpload.length; i++) {
        if (
          !this.files.find(
            file =>
              file.size === filesToUpload[i].size &&
              file.name === filesToUpload[i].name
          )
        ) {
          uniqueFiles.push(filesToUpload[i])
        } else {
          duplicateFiles.push(filesToUpload[i])
        }
      }
      return { uniqueFiles: uniqueFiles, duplicateFiles: duplicateFiles }
    },
    closeAlertDialog() {
      this.showAlertDialog = false
      this.alertList.UserHasNoPermission = []
      this.alertList.InvalidMetaTag = []
      this.alertList.PageCountValidationFailed = []
      this.alertList.FileSizeTooLarge = []
      this.alertList.WhiteLineWithoutSerialSeq = []
      this.alertList.MetaTagOnSerialLetter = []
      this.alertList.XMP = []
      this.alertList.WhiteLineNotParsable = []
      this.alertList.PdfFileEncrypted = []
    }
  }
}
</script>
<template>
  <v-card flat :loading="loading">
    <FileUploadDialog :files-in-transfer="filesInTransfer" />
    <UploadDialog
      :show-dialog.sync="showUploadDialog"
      :letter-category="letterCategory"
      :files="uploadedFiles"
      @submit="addMetaDataToFiles"
    />
    <AlertDialog
      :show-alert-dialog="showAlertDialog"
      :close-alert-dialog="closeAlertDialog"
      :alert-list="alertList"
    >
    </AlertDialog>

    <Dropzone
      :letter-category="letterCategory"
      :multiple="letterCategory !== 'series'"
      @dropped="upload($event)"
    />

    <v-card v-if="files.length > 0" class="mx-1 mt-2 uploadScrollbar">
      <v-list two-line flat dense>
        <v-list-item-group>
          <template v-for="(file, index) in files">
            <PdfListElement
              :id="letterCategory + 'ListElement' + index"
              ref="PdfListElements"
              :key="file.name"
              :file-id="file.id"
              :file-element="file"
              :delete-file="() => deleteFile(index)"
              :index="index"
              :letter-category="letterCategory"
              @changed="fileElementChanged"
            />
            <v-divider v-if="index + 1 < files.length" :key="index"></v-divider>
          </template>
        </v-list-item-group>
      </v-list>
    </v-card>
  </v-card>
</template>
<style lang="scss" scoped>
.uploadScrollbar {
  max-height: 500px;
  overflow-y: auto;
}
.slot-width {
  width: 270px;
}
</style>
